import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Button } from '@mui/material';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import { FileText } from 'lucide-react';

import { Cma_Report_Source_Enum_Enum } from '@graphql/__generated__/graphql';
import { CmaGenerationProgress } from '@modules/_shared/components/CmaGenerationProgress';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { GENERATE_CMA_REPORT } from '@modules/reports/cma-reports/cmaReportsQueries';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { MODULES_CMA_REPORTS } from '@shared/constants/module';
import { useLocale } from '@shared/hooks/locale';

import { LEAD_CMA_ANCHORS } from '../leadOneClickCmaTour';

type Props = {
  leadId: string;
  disabled?: boolean;
  isPreviewOpen?: boolean;
  onSuccess?: (cmaReportId: string) => void;
  onClose?: () => void;
  onError?: (error: ErrorLike) => void;
  refetch?: () => void;
};

export const GenerateCMAPreviewButton = ({
  leadId,
  disabled,
  isPreviewOpen = false,
  onSuccess,
  onClose,
  onError,
  refetch,
}: Props) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();
  const [hasGeneratedSuccessfully, setHasGeneratedSuccessfully] =
    useState(false);

  const [generateCmaReport, { loading }] = useMutation(GENERATE_CMA_REPORT, {
    onError,
  });

  const handleClick = useCallback(async () => {
    if (isPreviewOpen) {
      onClose?.();
      return;
    }

    const startTime = Date.now();

    const result = await generateCmaReport({
      variables: {
        leadId,
        source: Cma_Report_Source_Enum_Enum.LeadDetailPage,
      },
    });

    const cmaReportId = result.data?.generate_cma_report?.cma_report_id;

    if (cmaReportId) {
      setHasGeneratedSuccessfully(true);
      const baseProperties = getBaseCmaEventProperties(me, cmaReportId);

      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_CREATED, {
          ...baseProperties,
          source: Cma_Report_Source_Enum_Enum.LeadDetailPage,
          lead_id: leadId,
          time_to_create_ms: Date.now() - startTime,
        });
      }

      onSuccess?.(cmaReportId);
      refetch?.();
    }
  }, [
    isPreviewOpen,
    onClose,
    generateCmaReport,
    leadId,
    me,
    track,
    onSuccess,
    refetch,
  ]);

  const hasCMAModule = hasActiveModuleGrant(
    me?.module_grants,
    MODULES_CMA_REPORTS,
  );

  // Plays when the `cma-one-click-from-lead` campaign targets this user.
  useAutoTour('lead-one-click-cma', hasCMAModule && disabled !== true);

  if (!hasCMAModule) {
    return null;
  }

  const showButton = !loading && !hasGeneratedSuccessfully;

  return (
    <Box>
      {showButton && (
        <Button
          {...tourAnchor(LEAD_CMA_ANCHORS.createCma)}
          variant="contained"
          startIcon={<FileText />}
          onClick={handleClick}
          disabled={disabled}
          fullWidth
        >
          {t('Create CMA report')}
        </Button>
      )}
      <CmaGenerationProgress
        isLoading={loading || hasGeneratedSuccessfully}
        variant="inline"
      />
    </Box>
  );
};
